import Keycloak from 'keycloak-js'
import { ref, type Ref } from 'vue'

export interface KeycloakState {
  accessToken: string
  refreshToken: string
  tokenParsed: {
    roles?: string[]
    realm_access?: { roles?: string[] }
    resource_access?: Record<string, { roles?: string[] }>
  }
}

let kc: Keycloak | null = null

export function useKeycloak() {
  const state = ref<KeycloakState | null>(null) as Ref<KeycloakState | null>
  const error = ref<string | null>(null)
  const isReady = ref(false)

  async function init(config: {
    url: string
    realm: string
    clientId: string
  }): Promise<void> {
    if (kc) {
      kc = null
    }

    kc = new Keycloak({
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    })

    try {
      const authenticated = await kc.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
        pkceMethod: 'S256',
      })

      if (!authenticated) {
        throw new Error('Not authenticated')
      }

      if (!kc.token) {
        throw new Error('No token received')
      }

      state.value = {
        accessToken: kc.token,
        refreshToken: kc.refreshToken ?? '',
        tokenParsed: kc.tokenParsed as KeycloakState['tokenParsed'],
      }

      const minValidity = 30
      kc.onTokenExpired = () => {
        kc?.updateToken(minValidity).catch(() => {
          kc?.login()
        })
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Keycloak init failed'
      throw e
    } finally {
      isReady.value = true
    }
  }

  function logout() {
    kc?.logout()
    state.value = null
  }

  function getAccessToken(): string | null {
    return state.value?.accessToken ?? null
  }

  function hasRole(role: string): boolean {
    if (!state.value) return false

    // FirstMIS tokens carry roles in a top-level `roles` claim
    // (custom user-attribute mapper), same as the main frontend.
    const customRoles = state.value.tokenParsed?.roles ?? []
    if (customRoles.includes(role)) return true

    // Fallback to standard Keycloak locations for safety.
    const realmRoles = state.value.tokenParsed?.realm_access?.roles ?? []
    if (realmRoles.includes(role)) return true

    const resourceAccess = state.value.tokenParsed?.resource_access ?? {}
    for (const resource of Object.values(resourceAccess)) {
      if (resource?.roles?.includes(role)) return true
    }
    return false
  }

  return {
    state: state as Ref<KeycloakState | null>,
    error,
    isReady,
    init,
    logout,
    getAccessToken,
    hasRole,
  }
}
